import './sentry';
import { StrictMode, Component } from 'react';
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import '@fontsource-variable/geist/index.css';
import '@fontsource-variable/geist-mono/index.css';
import '@fontsource/caveat/400.css';
import '@fontsource/caveat/500.css';
import '@fontsource/caveat/600.css';
import './index.css';
import App from './App.tsx';
import { getClerkRuntimeProblem } from './lib/clerkConfig';
import { clerkAppearance } from './components/auth/clerkAppearance';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; message?: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry in production, console in development
    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    } else {
      console.error('App error:', error, errorInfo);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, color: '#fff', background: 'linear-gradient(135deg,#0f172a,#3b0764,#86198f)', minHeight: '100vh' }}>
          <h1 style={{ fontWeight: 900, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ opacity: 0.8 }}>An unexpected error occurred while rendering the app.</p>
          {this.state.message ? <pre style={{ opacity: 0.8, marginTop: 12 }}>{this.state.message}</pre> : null}
        </div>
      );
    }
    return this.props.children;
  }
}

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkRuntimeProblem = getClerkRuntimeProblem({
  publishableKey: clerkPublishableKey,
  hostname: window.location.hostname,
});
const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container not found');
}

const root = createRoot(container);

function AppProviders({ children }: { children: ReactNode }) {
  if (clerkRuntimeProblem) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#faf7f2',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 560,
            borderRadius: 24,
            border: '1px solid rgba(45, 34, 25, 0.1)',
            background: '#fff',
            padding: 32,
            boxShadow: '0 20px 40px rgba(45, 34, 25, 0.08)',
          }}
        >
          <p style={{ margin: 0, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a39585' }}>
            The Lab
          </p>
          <h1 style={{ margin: '12px 0 8px', color: '#2d2219' }}>{clerkRuntimeProblem.title}</h1>
          <p style={{ margin: 0, color: '#6b5d4f', lineHeight: 1.6 }}>{clerkRuntimeProblem.message}</p>
        </div>
      </div>
    );
  }

  if (clerkPublishableKey) {
    return (
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        afterSignOutUrl="/"
        appearance={clerkAppearance}
        localization={{ signIn: { start: { title: 'Sign in', subtitle: 'Continue to your courses and saved work.' } } }}
      >
        {children}
      </ClerkProvider>
    );
  }

  return <>{children}</>;
}

const app = (
  <StrictMode>
    <ErrorBoundary>
      <AppProviders>
        <App />
        <SpeedInsights />
      </AppProviders>
    </ErrorBoundary>
  </StrictMode>
);

root.render(app);
